import { type FC, useCallback, useRef, useState } from 'react';

import { _isNil, _isNotBlank, _notNil } from '@/littledash';
import { errorToast, successToast } from '@/helpers';
import InVivoError from '@/model/InVivoError.ts';
import { ApiService } from '@/support/ApiService.ts';
import { apiErrorToast } from '@/components/UI/ApiErrorToast/ApiErrorToast';
import { ExceptionHandler } from '@/utils/ExceptionHandler.ts';
import { ModalActions, ModalContainer, ModalHeader } from '@/utils/modal';
import type { TreatmentTypeRow } from '@/utils/treatmentTypeValidation';
import { validateGridBeforeSave } from '@/utils/treatmentBulkSave.utils';

import BulkAddTreatmentsTable from './BulkAddTreatmentsTable';

interface HotTableRefLike {
  hotInstance?: {
    getSourceData?: () => Array<TreatmentTypeRow>;
    countRows?: () => number;
    validateCells?: (callback?: (valid: boolean) => void) => void;
    validateRows?: (rows: Array<number>, callback?: (valid: boolean) => void) => void;
  } | null;
}

interface BulkAddTreatmentsProps {
  onSave: () => void;
  onCancel: () => void;
}

/**
 * Modal for creating treatment glossary types in bulk from a spreadsheet grid.
 * Every non-blank row is sent in a single atomic create request.
 */
const BulkAddTreatments: FC<BulkAddTreatmentsProps> = ({ onSave, onCancel }) => {
  const [saving, setSaving] = useState(false);
  const [hasValidationErrors, setHasValidationErrors] = useState(false);
  const hotTableRef = useRef<HotTableRefLike | null>(null);

  const handleValidationChange = useCallback((invalid: boolean): void => {
    setHasValidationErrors(invalid);
  }, []);

  const handleSave = async () => {
    setSaving(true);
    let names: Array<string> = [];
    try {
      const hotInstance = hotTableRef.current?.hotInstance;
      if (_isNil(hotInstance)) {
        errorToast('Unable to access table data');
        return;
      }

      const valid = await validateGridBeforeSave(hotInstance, { spareRows: 1 });
      if (!valid) {
        errorToast('Please correct the highlighted treatments before creating');
        return;
      }

      const currentData = hotInstance.getSourceData?.() ?? [];
      names = currentData.reduce<Array<string>>((acc, { name }) => {
        if (_isNotBlank(name)) {
          acc.push(name.trim());
        }
        return acc;
      }, []);

      if (names.length === 0) {
        errorToast('Please add at least one treatment');
        return;
      }

      await ApiService.call({
        endpoint: 'POST /api/v1/team-glossary/treatment-types/create',
        path: undefined,
        body: { treatment_types: names.map((name) => ({ name })) },
        options: { onError: { toast: false, throw: true, capture: false, slug: 'bulk-add-treatments-save' } },
      });

      successToast(`Successfully added ${names.length} treatment${names.length === 1 ? '' : 's'}`);
      onSave();
    } catch (error) {
      apiErrorToast({
        title: 'Could not create treatments',
        error,
        // The server reports failures by their position in the payload just submitted.
        labelForDetail: ({ index }) => (_notNil(index) ? names[index] : undefined),
      });
      ExceptionHandler.captureException(
        new InVivoError('Failed to create treatments', { cause: error, slug: 'bulk-add-treatments-save' })
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <ModalContainer size="narrow" className="bg-white">
      <ModalHeader
        title="Create treatments"
        closeModal={onCancel}
        closeDisabled={saving}
        className="pa3 bb b--moon-gray w-100"
      />

      <div className="pa3">
        <BulkAddTreatmentsTable innerRef={hotTableRef} setInvalid={handleValidationChange} />
      </div>

      <ModalActions
        className="pa3 bt b--moon-gray w-100"
        onSubmit={handleSave}
        onCancel={onCancel}
        submitBtnText="Create"
        cancelBtnText="Cancel"
        submitButtonProps={{
          loading: saving,
          disabled: saving || hasValidationErrors,
          testId: 'bulk-add-treatments-submit',
        }}
        cancelButtonProps={{
          disabled: saving,
          testId: 'bulk-add-treatments-cancel',
        }}
      />
    </ModalContainer>
  );
};

export default BulkAddTreatments;
